import { supabase } from '../lib/supabase';
import type { Session } from '@supabase/supabase-js';

type TimeoutHandle = ReturnType<typeof setTimeout> | null;

type InitializeOptions = {
  onIdleTimeout: () => void;
};

const ACCESS_COOKIE = 'tn_at';
const REFRESH_COOKIE = 'tn_rt';
const ROLE_COOKIE = 'tn_role';

const ACCESS_TOKEN_MAX_AGE = 15 * 60; // 15 minutes in seconds
const REFRESH_TOKEN_MAX_AGE = 7 * 24 * 60 * 60; // 7 days in seconds
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const REFRESH_HEADROOM_MS = 2 * 60 * 1000; // refresh at least 2 minutes before expiry
const IDLE_WARNING_MS = 25 * 60 * 1000; // 25 minutes
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const IDLE_WARNING_EVENT = 'tn-idle-warning';
const IDLE_TIMEOUT_EVENT = 'tn-idle-timeout';

let refreshTimer: TimeoutHandle = null;
let idleWarningTimer: TimeoutHandle = null;
let idleTimeoutTimer: TimeoutHandle = null;
let activityListenersAttached = false;
let idleTimeoutCallback: (() => void) | null = null;
let currentRoleScope: string | null = null;

const browserSafe = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  if (!browserSafe()) return;
  const encoded = encodeURIComponent(value);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encoded}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
};

const clearCookie = (name: string) => {
  if (!browserSafe()) return;
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

const emitLifecycleEvent = (eventName: string) => {
  if (!browserSafe()) return;
  window.dispatchEvent(new CustomEvent(eventName));
};

const clearRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const clearIdleTimers = () => {
  if (idleWarningTimer) {
    clearTimeout(idleWarningTimer);
    idleWarningTimer = null;
  }
  if (idleTimeoutTimer) {
    clearTimeout(idleTimeoutTimer);
    idleTimeoutTimer = null;
  }
};

const startIdleTimers = () => {
  if (!browserSafe()) return;
  clearIdleTimers();
  idleWarningTimer = setTimeout(() => {
    emitLifecycleEvent(IDLE_WARNING_EVENT);
  }, IDLE_WARNING_MS);

  idleTimeoutTimer = setTimeout(async () => {
    emitLifecycleEvent(IDLE_TIMEOUT_EVENT);
    if (idleTimeoutCallback) {
      idleTimeoutCallback();
    }
  }, IDLE_TIMEOUT_MS);
};

const handleActivity = () => {
  recordUserActivity();
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    recordUserActivity();
  }
};

const attachActivityListeners = () => {
  if (!browserSafe() || activityListenersAttached) return;
  const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'wheel', 'scroll', 'touchstart'];
  events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));
  window.addEventListener('visibilitychange', onVisibilityChange);
  activityListenersAttached = true;
};

const detachActivityListeners = () => {
  if (!browserSafe() || !activityListenersAttached) return;
  const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'wheel', 'scroll', 'touchstart'];
  events.forEach((event) => window.removeEventListener(event, handleActivity));
  window.removeEventListener('visibilitychange', onVisibilityChange);
  activityListenersAttached = false;
};

const refreshSessionTokens = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('[AuthSession] Refresh failed:', error.message);
      clearSessionStorage();
      if (idleTimeoutCallback) {
        idleTimeoutCallback();
      }
      return;
    }

    if (data.session) {
      applySession(data.session, currentRoleScope);
    }
  } catch (err) {
    console.error('[AuthSession] Refresh exception:', err);
    clearSessionStorage();
    if (idleTimeoutCallback) {
      idleTimeoutCallback();
    }
  }
};

const scheduleRefresh = (session: Session) => {
  if (!browserSafe()) return;
  clearRefreshTimer();
  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : Date.now() + ACCESS_TOKEN_MAX_AGE * 1000;
  const desiredRefreshTime = expiresAtMs - REFRESH_HEADROOM_MS;
  const delay = Math.max(REFRESH_INTERVAL_MS, desiredRefreshTime - Date.now());
  refreshTimer = setTimeout(() => {
    void refreshSessionTokens();
  }, delay);
};

export const initializeAuthSessionManager = ({ onIdleTimeout }: InitializeOptions): (() => void) => {
  idleTimeoutCallback = onIdleTimeout;
  attachActivityListeners();
  startIdleTimers();

  return () => {
    clearRefreshTimer();
    clearIdleTimers();
    detachActivityListeners();
    idleTimeoutCallback = null;
  };
};

const clearRoleCookie = () => {
  clearCookie(ROLE_COOKIE);
  currentRoleScope = null;
};

export const applySession = (session: Session, roleScope?: string | null) => {
  if (!browserSafe()) return;
  setCookie(ACCESS_COOKIE, session.access_token, ACCESS_TOKEN_MAX_AGE);
  setCookie(REFRESH_COOKIE, session.refresh_token, REFRESH_TOKEN_MAX_AGE);

  if (roleScope) {
    setRoleScope(roleScope);
  } else {
    clearRoleCookie();
  }

  scheduleRefresh(session);
  startIdleTimers();
};

export const clearSessionStorage = () => {
  clearRefreshTimer();
  clearIdleTimers();
  clearCookie(ACCESS_COOKIE);
  clearCookie(REFRESH_COOKIE);
  clearRoleCookie();
};

export const setRoleScope = (roleScope: string) => {
  currentRoleScope = roleScope;
  setCookie(ROLE_COOKIE, roleScope, REFRESH_TOKEN_MAX_AGE);
};

export const clearRoleScope = () => {
  clearRoleCookie();
};

export const recordUserActivity = () => {
  if (!browserSafe()) return;
  startIdleTimers();
};

export const getCurrentRoleScope = (): string | null => currentRoleScope;

export const getIdleWarningEventName = (): string => IDLE_WARNING_EVENT;
export const getIdleTimeoutEventName = (): string => IDLE_TIMEOUT_EVENT;
